import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, UserPlus, SignOut, Crown, Trash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Household() {
  const { user, refresh } = useAuth();
  const [hh, setHh] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/household");
    setHh(data);
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/household/invite", { email });
      toast.success(`Invited ${email}`);
      setEmail(""); load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this member?")) return;
    try { await api.post(`/household/remove/${id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const leave = async () => {
    if (!confirm("Leave this household? You'll lose family-plan access.")) return;
    try { await api.post("/household/leave"); await refresh(); load(); } catch (e) { toast.error(formatError(e)); }
  };

  if (!hh) return <div className="text-stoneink-500">Loading…</div>;

  // Only family plan owners can manage household; everyone else sees CTA
  if (hh.role === "owner" && user?.tier === "family") {
    return (
      <div className="space-y-8" data-testid="household-page">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Your household</h1>
          <p className="mt-2 text-stoneink-700">Invite up to {hh.max_members} family members to share your inventory.</p>
        </div>

        <form onSubmit={invite} className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Member email</label>
            <input
              data-testid="invite-email-input"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="partner@home.app"
              className="mt-2 w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
            />
          </div>
          <button data-testid="invite-btn" disabled={busy || hh.members.length >= hh.max_members}
            className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 disabled:opacity-60">
            <UserPlus weight="bold" /> Invite
          </button>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-terracotta-500 text-white grid place-items-center"><Crown weight="duotone" size={20} /></div>
              <div>
                <div className="font-heading text-lg text-stoneink-900 font-medium">{user?.name} (you)</div>
                <div className="text-sm text-stoneink-500">Owner · {user?.email}</div>
              </div>
            </div>
          </motion.div>
          {hh.members.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i }}
              className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient flex items-center justify-between"
              data-testid={`member-${m.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-sage-400/20 text-sage-600 grid place-items-center"><Users weight="duotone" size={20} /></div>
                <div>
                  <div className="font-heading text-lg text-stoneink-900 font-medium">{m.name}</div>
                  <div className="text-sm text-stoneink-500">{m.email}</div>
                </div>
              </div>
              <button onClick={() => remove(m.id)} className="p-2 rounded-lg hover:bg-sand-200 text-stoneink-500 hover:text-destructive">
                <Trash size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (hh.role === "member") {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="household-page">
        <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Household</h1>
        <div className="rounded-3xl border border-sand-300 bg-white p-6 shadow-ambient">
          <div className="text-stoneink-700">You&apos;re a member of <span className="font-medium text-stoneink-900">{hh.owner?.name}</span>&apos;s household.</div>
          <div className="text-stoneink-500 text-sm mt-1">{hh.owner?.email}</div>
          <button onClick={leave} className="mt-5 h-11 px-5 rounded-xl bg-white border border-sand-300 hover:border-destructive hover:text-destructive text-stoneink-700 flex items-center gap-2">
            <SignOut size={16} /> Leave household
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="household-page">
      <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Household</h1>
      <div className="rounded-3xl border border-sand-300 bg-white p-8 shadow-ambient">
        <Users weight="duotone" size={42} className="text-stoneink-500" />
        <div className="mt-3 font-heading text-2xl font-medium text-stoneink-900">Share with family</div>
        <p className="mt-2 text-stoneink-700">Family plan lets you share your inventory with up to 5 household members.</p>
        <Link to="/pricing" data-testid="upgrade-family-btn" className="mt-6 inline-flex items-center gap-2 h-12 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium">
          See Family plan →
        </Link>
      </div>
    </div>
  );
}
